import { useEffect, useState } from 'react';

import { BUCKET_STROKE, ringArcs, type Coverage } from '../commitCoverage';
import { COMMIT_SUMMARY_COPY as WORDS } from '../linkedAccountsCopy';

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Everyone in the organization as one ring (spec 164 US1): a segment per
 * bucket, pushing as themselves first, so the green arc is the answer. Named
 * as a sentence for assistive technology; the legend beside it carries the
 * same counts in words, so colour is never the only signal.
 *
 * It draws in once, as the Overview's ring does (spec 155 FR-013): the arcs
 * start empty and grow on the next frame, unless the person asked for less
 * motion.
 */
export function CoverageRing({
  coverage,
  size = 96,
}: {
  coverage: Coverage;
  size?: number;
}) {
  const radius = 40;
  const stroke = 9;
  const circumference = 2 * Math.PI * radius;
  const [drawn, setDrawn] = useState(reducedMotion);
  useEffect(() => {
    if (drawn) return;
    const frame = requestAnimationFrame(() => {
      setDrawn(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [drawn]);

  const arcs = ringArcs(coverage, circumference);

  return (
    <svg
      role="img"
      aria-label={WORDS.ringLabel(coverage.themselves, coverage.total)}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className="shrink-0"
    >
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        strokeWidth={stroke}
        className="stroke-muted"
      />
      {arcs.map((arc) => (
        <circle
          key={arc.bucket}
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={`${String(drawn ? arc.length : 0)} ${String(circumference)}`}
          strokeDashoffset={-arc.start}
          data-ring-segment={arc.bucket}
          className={`${BUCKET_STROKE[arc.bucket]} motion-safe:ease-standard motion-safe:transition-[stroke-dasharray] motion-safe:duration-slow`}
          transform="rotate(-90 50 50)"
        />
      ))}
      <text
        x="50"
        y="50"
        textAnchor="middle"
        dominantBaseline="central"
        aria-hidden="true"
        className="fill-foreground text-2xl font-light"
      >
        {coverage.total > 0
          ? `${String(Math.round((coverage.themselves / coverage.total) * 100))}%`
          : '–'}
      </text>
    </svg>
  );
}
